import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Dumbbell, Moon, Sun, PenLine, Sparkles, X, Loader2, PawPrint } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ICONS = { dumbbell: Dumbbell, moon: Moon, sun: Sun, pen: PenLine, paw: PawPrint };

export function NanoukInbox() {
    const { language } = useLanguage();
    const navigate = useNavigate();
    const de = language === 'de';
    const [items, setItems] = useState([]);
    const [busy, setBusy] = useState(null);

    const load = useCallback(async (coords) => {
        try {
            const params = coords ? `?lat=${coords.latitude.toFixed(3)}&lon=${coords.longitude.toFixed(3)}` : '';
            const res = await axios.get(`${API}/inbox${params}`);
            setItems(res.data.items || []);
        } catch { /* silent */ }
    }, []);

    useEffect(() => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (pos) => load(pos.coords),
                () => load(null),
                { timeout: 5000, maximumAge: 600000 }
            );
        } else {
            load(null);
        }
    }, [load]);

    const approve = async (item) => {
        if (item.action_type === 'link') {
            axios.post(`${API}/inbox/${item.id}/approve`).catch(() => {});
            navigate(item.payload?.url || '/blog-studio');
            return;
        }
        setBusy(item.id);
        try {
            const res = await axios.post(`${API}/inbox/${item.id}/approve`);
            setItems((prev) => prev.filter((i) => i.id !== item.id));
            if (res.data.logged === 'workout') {
                toast.success(de ? `Workout geloggt! +${res.data.points} Punkte 💪` : `Workout logged! +${res.data.points} points 💪`);
            } else if (res.data.created === 'task') {
                toast.success(de ? 'Aufgabe angelegt ✓' : 'Task created ✓');
            } else {
                toast.success('✓');
            }
        } catch {
            toast.error(de ? 'Das hat nicht geklappt' : 'That did not work');
        } finally {
            setBusy(null);
        }
    };

    const dismiss = async (item) => {
        setItems((prev) => prev.filter((i) => i.id !== item.id));
        axios.post(`${API}/inbox/${item.id}/dismiss`).catch(() => {});
    };

    if (items.length === 0) return null;

    return (
        <div className="space-y-2" data-testid="nanouk-inbox">
            <div className="flex items-center gap-1.5 px-1">
                <Sparkles className="w-4 h-4 text-violet-500" />
                <span className="text-xs font-bold uppercase tracking-wide text-violet-600">
                    {de ? 'Nanouk hat vorbereitet' : 'Nanouk prepared this'}
                </span>
            </div>
            <AnimatePresence>
                {items.map((item) => {
                    const Icon = ICONS[item.icon] || Sparkles;
                    return (
                        <motion.div
                            key={item.id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, x: 60 }}
                            className="rounded-2xl border border-violet-200 bg-gradient-to-br from-violet-50 to-indigo-50 p-3.5"
                            data-testid={`inbox-item-${item.source}`}
                        >
                            <div className="flex items-start gap-3">
                                <div className="shrink-0 w-9 h-9 rounded-xl bg-violet-100 flex items-center justify-center">
                                    <Icon className="w-4.5 h-4.5 text-violet-600" />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <p className="text-sm font-bold leading-snug">{item.title?.[language] || item.title?.de}</p>
                                    <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{item.body?.[language] || item.body?.de}</p>
                                    <div className="flex items-center gap-2 mt-2.5">
                                        <button
                                            onClick={() => approve(item)}
                                            disabled={busy === item.id}
                                            className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full bg-violet-600 text-white hover:bg-violet-700 transition-colors disabled:opacity-50"
                                            data-testid={`inbox-approve-${item.source}`}
                                        >
                                            {busy === item.id && <Loader2 className="w-3 h-3 animate-spin" />}
                                            {item.action_label?.[language] || item.action_label?.de}
                                        </button>
                                        <button
                                            onClick={() => dismiss(item)}
                                            className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-full text-muted-foreground hover:bg-white/60 transition-colors"
                                            data-testid={`inbox-dismiss-${item.source}`}
                                        >
                                            <X className="w-3 h-3" />
                                            {de ? 'Heute nicht' : 'Not today'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    );
                })}
            </AnimatePresence>
        </div>
    );
}
